import { AsyncPipe, NgClass, NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, ElementRef, inject, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Store } from '@ngrx/store';
import { ModuleIcons } from '@unified-ui/shared/asset-lib';
import { BuilderManagerService } from '@unified-ui/shared/services';
import { MenuItem } from 'primeng/api';
import { ContextMenu, ContextMenuModule } from 'primeng/contextmenu';
import { Tabs, TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { combineLatest, filter, map, take } from 'rxjs';
import { LandingPageComponent } from '../landing-page/landing-page.component';
import { NavigationRoutes } from './navigation-routes';
import { NavigationTabsService } from './navigation-tabs.service';
import { switchToLastTab } from './store/actions/report-tabs.actions';
import { IBaseTab, ITabDashboard, ITabReport, ReportTabActions, ReportTabSelectors, TabTypeEnum } from './store/index';

enum ContextMenuLabels {
    CLOSE = 'Close',
    CLOSE_OTHERS = 'Close Others',
    CLOSE_RIGHT = 'Close to the Right',
    CLOSE_SAVED = 'Close Saved',
    CLOSE_ALL = 'Close All'
}

@Component({
    selector: 'unified-ui-navigation-tabs',
    standalone: true,
    imports: [AsyncPipe, NgClass, NgComponentOutlet, RouterOutlet, TabsModule, TooltipModule, ContextMenuModule, LandingPageComponent],
    templateUrl: './navigation-tabs.component.html',
    styleUrl: './navigation-tabs.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavigationTabsComponent {
    private _store = inject(Store);
    private _builderManager = inject(BuilderManagerService);
    private _navigationTabsService = inject(NavigationTabsService);
    private _router = inject(Router);
    private _changeDetectorReference = inject(ChangeDetectorRef);
    private _elementRef = inject(ElementRef);

    public readonly pTabs = viewChild<Tabs>('pTabs');
    public readonly tabPanel = viewChild<HTMLElement>('tabPanel');
    public readonly contextMenu = viewChild<ContextMenu>('contextMenu');

    protected tabRightClick(mouseEvent: MouseEvent) {
        if (this.contextMenu()?.container) {
            return;
        }

        if (mouseEvent.button === 2 && this._elementRef.nativeElement.contains(mouseEvent.target)) {
            const tabNode = this._findTabNode(mouseEvent.target);

            if (tabNode) {
                const selectedIndex = Number((tabNode as any).dataset['index']);
                if (selectedIndex === 0) {
                    /*Home tab*/ this.showContextMenu = false;
                    return;
                }
                if (!this.showContextMenu) {
                    this.showContextMenu = true;
                }
            } else {
                this.showContextMenu = false;
            }
        } else {
            this.showContextMenu = false;
        }
    }

    private _findTabNode(target: EventTarget | null): EventTarget | undefined {
        if (!target) return undefined;
        
        const element = target as HTMLElement;
        if (element.classList?.contains('header-wrapper')) {
            return target;
        }
        if (element.parentNode && (element.parentNode as HTMLElement).classList?.contains('header-wrapper')) {
            return element.parentNode;
        }
        return undefined;
    }

    public tabs$ = combineLatest([this._store.select(ReportTabSelectors.selectCurrentTabId), this._store.select(ReportTabSelectors.selectTabEntities)]).pipe(
        takeUntilDestroyed(),
        map(([currentTabId, tabEntities]) => {
            // Convert the currentTabId to the actual index in the tab array for proper selection
            const tabValues = Object.values(tabEntities) as IBaseTab<ITabDashboard | ITabReport>[];
            this._tabs = tabValues;

            // Find the index of the tab with the matching tabId
            const tabIndex = tabValues.findIndex(tab => tab.tabId === currentTabId);
            if (tabIndex !== -1) {
                // Use the tabId as the value for PrimeNG tabs
                this.activeIndex = currentTabId as string;

                // If this is a newly created tab (not HOME), we need to ensure it gets focus
                if (currentTabId !== 'HOME' && currentTabId !== this._lastSelectedTabId) {
                    this._lastSelectedTabId = currentTabId as string;
                    // Use setTimeout to ensure the tabs are rendered before trying to select the tab
                    setTimeout(() => {
                        this.selectTabById(currentTabId as string);
                    }, 0);
                }
            } else if (currentTabId === 'HOME' && tabValues.some(tab => tab.tabId === 'HOME')) {
                this.activeIndex = 'HOME';
            } else {
                // If we can't find the tab, default to the first tab (which should be HOME)
                const homeTab = tabValues.find(tab => tab.tabId === 'HOME');
                if (homeTab) {
                    this.activeIndex = 'HOME';
                    // Force selection of HOME tab in the store
                    this._store.dispatch(ReportTabActions.switchTabAction({ id: 'HOME' }));
                }
            }
            return tabValues;
        })
    );
    public tabInfo$ = this._store.select(ReportTabSelectors.getTabInfo);

    protected contentTypes: { [k: string]: any } = {};
    protected activeIndex: string | number = 0;
    protected homeTabLabel = '';
    protected homeTabIcon = '';
    protected showContextMenu = false;
    protected contextMenuItems: MenuItem[] = [];
    protected readonly tabTypeEnum = TabTypeEnum;

    private _tabs: IBaseTab<ITabReport | ITabDashboard>[] = [];
    private _lastSelectedTabId: string = '';

    constructor() {
        this._handleRouteNavigation();
        this._handleTabClose();
        effect(() => {
            this.contentTypes = this._navigationTabsService._contentTypes();
        });
    }

    private _handleRouteNavigation(): void {
        this._router.events
            .pipe(
                takeUntilDestroyed(),
                filter(event => event instanceof NavigationEnd)
            )
            .subscribe(navEnd => {
                // Identify valid routes to redirect if needed
                const activeRoute = (navEnd as NavigationEnd).urlAfterRedirects;
                const validRoutes = Object.values(NavigationRoutes);
                if (!validRoutes.includes(activeRoute)) {
                    this._router.navigate(['/']);
                }

                this.homeTabLabel = activeRoute.split('/').pop() || '';
                this.homeTabIcon = ModuleIcons[this.homeTabLabel.toUpperCase() as keyof typeof ModuleIcons] || ModuleIcons.HOME;
                const home: IBaseTab<ITabDashboard> = {
                    tabId: 'HOME',
                    icon: this.homeTabIcon,
                    closeable: false,
                    content: <ITabDashboard>{
                        name: ''
                    },
                    tabType: TabTypeEnum.Home
                };
                this._navigationTabsService.createTab(home);

                // Explicitly select the HOME tab when it's created or when navigating to a route that should show the home tab
                if (activeRoute === '/' || activeRoute.startsWith(NavigationRoutes.home)) {
                    this._navigationTabsService.switchTab('', 'HOME');

                    // Use setTimeout to ensure the tabs are rendered before trying to select one
                    setTimeout(() => {
                        this.selectHomeTab();
                    }, 0);
                }

                this._changeDetectorReference.detectChanges();
            });
    }

        // Listen for tab close events to set focus on the next tab
    private _handleTabClose(): void {
        effect(() => {
            const closedTab = this._navigationTabsService._tabClosed();
            if (closedTab.id) {
                // Get all available tabs to determine which one to select next
                this._store
                    .select(ReportTabSelectors.selectAllTabs)
                    .pipe(take(1))
                    .subscribe(allTabs => {
                        // Filter out the HOME tab unless it's the only one left
                        const nonHomeTabs = allTabs.filter(tab => tab.tabId !== 'HOME');

                        if (nonHomeTabs.length > 0) {
                            // If there are non-HOME tabs, select the first one
                            const nextTabId = nonHomeTabs[0].tabId;
                            this._navigationTabsService.switchTab(nonHomeTabs[0].content.type || '', nextTabId);

                            // Set focus to the selected non-HOME tab
                            setTimeout(() => {
                                this.selectTabById(nextTabId);
                            }, 0);
                        } else if (allTabs.length > 0) {
                            // If only HOME tab is left, select it
                            this._navigationTabsService.switchTab('', 'HOME');

                            // Set focus to the HOME tab
                            setTimeout(() => {
                                this.selectTabById('HOME');
                            }, 0);
                        }
                    });
            }
        });
    }

    /**
     * Triggers the dispatch for navigating
     * @param tabIdOrTab Tab ID or Tab data
     */
    public onTabChange(tabIdOrTab: string | number | IBaseTab<ITabReport | ITabDashboard>): void {
        // Handle both cases: when receiving a tab ID (string/number) or a tab object
        let tabId: string;
        let selectedTabType: string;

        if (typeof tabIdOrTab === 'string' || typeof tabIdOrTab === 'number') {
            // We received a tab ID directly
            tabId = tabIdOrTab.toString();
            selectedTabType = this.getTabTypeById(tabId);
        } else {
            // We received a tab object
            tabId = tabIdOrTab.tabId || '';
            if (!tabId) {
                return;
            }
            selectedTabType = this.getTabTypeById(tabId);
        }

        this._navigationTabsService.switchTab(selectedTabType, tabId);
    }

    /**
     * close the selected tab
     */
    public onTabClose(tab: IBaseTab<ITabReport | ITabDashboard>): void {
        const selectedTabType = this.getTabTypeById(tab.tabId);
        this._store
            .select(ReportTabSelectors.getTabInfo)
            .pipe(take(1))
            .subscribe(tabInfo => {
                if (tabInfo[tab.tabId]?.unsaved) {
                    this._navigationTabsService.addToTabCloseOnSave(tab.tabId);
                    this._builderManager.unsavedWarningStateData({ open: true, tabId: tab.tabId });
                } else {
                    this._navigationTabsService.closeTab(selectedTabType, tab.tabId);
                }
            });
    }

    /**
     * Right click on tab
     * @param mouseEvent MouseEvent
     */
    public contextMenuRightClick(mouseEvent: MouseEvent): void {
        const tabNode = this._findTabNode(mouseEvent.target);

        if (tabNode) {
            const dataset = (tabNode as any).dataset;
            // Prevent the browser context menu from showing on the tab
            if (dataset['tabid'] === 'HOME') {
                mouseEvent.stopPropagation();
                mouseEvent.preventDefault();
                return;
            }

            this.buildContextMenu(dataset);
        }
    }

    /**
     * Build Context Menu items for clicked tab
     * @param dataset DOMStringMap
     */
    public buildContextMenu(dataset: DOMStringMap): void {
        const selectedTabIndex = Number(dataset['index']);
        const tabsLength = Number(dataset['tablength']);
        let selectedTabId = dataset['tabid'] || '';

        this.contextMenuItems = [];

        this._store
            .select(ReportTabSelectors.getTabInfo)
            .pipe(take(1))
            .subscribe(tabInfo => {
                this.contextMenuItems = [
                    {
                        label: ContextMenuLabels.CLOSE,
                        command: () => {
                            if (tabInfo[selectedTabId as string].unsaved) {
                                this._navigationTabsService.addToTabCloseOnSave(selectedTabId);
                                this._builderManager.unsavedWarningStateData({ open: true, tabId: selectedTabId });
                            } else {
                                const selectedTabType = this.getTabTypeById(selectedTabId);
                                this._navigationTabsService.closeTab(selectedTabType, selectedTabId as string);
                            }
                        }
                    },
                    {
                        label: ContextMenuLabels.CLOSE_OTHERS,
                        disabled: tabsLength <= 2,
                        command: () => {
                            for (const [element] of Object.entries(tabInfo)) {
                                if (element !== selectedTabId && element !== 'HOME') {
                                    if (tabInfo[element as string].unsaved) {
                                        this._navigationTabsService.addToTabCloseOnSave(element);
                                        this._builderManager.unsavedWarningStateData({ open: true, tabId: element });
                                        this._store.dispatch(ReportTabActions.setToClosed({ id: element }));
                                        const selectedTabType = this.getTabTypeById(element);
                                        this._navigationTabsService.switchTab(selectedTabType, element);
                                    } else {
                                        const selectedTabType = this.getTabTypeById(element);
                                        this._navigationTabsService.closeTab(selectedTabType, element as string);
                                    }
                                }
                            }
                        }
                    },
                    {
                        label: ContextMenuLabels.CLOSE_RIGHT,
                        disabled: selectedTabIndex === tabsLength - 1,
                        command: () => {
                            for (const [element] of Object.entries(tabInfo)) {
                                if (element !== 'HOME' && tabInfo[element as string].index > selectedTabIndex) {
                                    if (tabInfo[element as string].unsaved) {
                                        this._navigationTabsService.addToTabCloseOnSave(element);
                                        selectedTabId = element;
                                        this._builderManager.unsavedWarningStateData({ open: true, tabId: element });
                                    } else {
                                        const selectedTabType = this.getTabTypeById(element);
                                        this._navigationTabsService.closeTab(selectedTabType, element as string);
                                    }
                                }
                            }
                            const selectedTabType = this.getTabTypeById(selectedTabId);
                            this._navigationTabsService.switchTab(selectedTabType, selectedTabId);
                        }
                    },
                    {
                        label: ContextMenuLabels.CLOSE_SAVED,
                        command: () => {
                            for (const [element] of Object.entries(tabInfo)) {
                                if (element !== 'HOME' && !tabInfo[element as string].unsaved) {
                                    const selectedTabType = this.getTabTypeById(element);
                                    this._navigationTabsService.closeTab(selectedTabType, element as string);
                                }
                            }
                            this._store.dispatch(switchToLastTab());
                        }
                    },
                    {
                        label: ContextMenuLabels.CLOSE_ALL,
                        command: () => {
                            for (const [element] of Object.entries(tabInfo)) {
                                if (element !== 'HOME') {
                                    if (tabInfo[element as string].unsaved) {
                                        this._navigationTabsService.addToTabCloseOnSave(element);
                                        this._builderManager.unsavedWarningStateData({ open: true, tabId: element });
                                    } else {
                                        const selectedTabType = this.getTabTypeById(element);
                                        this._navigationTabsService.closeTab(selectedTabType, element as string);
                                    }
                                }
                            }
                            this._store.dispatch(switchToLastTab());
                        }
                    }
                ];
            });
    }

    /**
     * Returns the content type for a given tab id
     * @param id tab id
     * @returns content identifier
     */

    /**
     * Directly selects the Home tab using the ViewChild reference to pTabs
     * This is a more direct approach than relying on the store and reactive data flow
     */
    private selectHomeTab(): void {
        this.selectTabById('HOME');
    }

    /**
     * Directly selects a tab by its ID using DOM manipulation
     * @param tabId The ID of the tab to select
     */
    private selectTabById(tabId: string): void {
        if (this.pTabs()) {
            // Force the activeIndex to be the tabId
            this.activeIndex = tabId;

            // Force change detection to update the view
            this._changeDetectorReference.detectChanges();

            // Find the index of the tab
            const tabIndex = this._tabs.findIndex(tab => tab.tabId === tabId);
            if (tabIndex !== -1) {
                // Access the native element if needed for direct DOM manipulation
                setTimeout(() => {
                    // Check the tabviewport, if overflowing add the tab-overflow class to add 34px to
                    // allow last tab's close button to be in view if the right scroll button doesn't auto disappear
                    const tabList = this._elementRef.nativeElement.querySelector('.p-tablist');
                    const tabListViewport = this._elementRef.nativeElement.querySelector('.p-tablist-viewport');
                    if (tabList && tabListViewport) {
                    const overflow = tabListViewport.scrollWidth > tabListViewport.clientWidth;
                    if (overflow) {
                        tabList.classList.add('tab-overflow');
                    } else {
                        tabList.classList.remove('tab-overflow');
                    }
                    }
                    // scroll tab in view in case it is created out ov view
                    const tabElements = this._elementRef.nativeElement.querySelectorAll('p-tab');
                    if (tabElements && tabElements.length > tabIndex) {
                        const tabElement = tabElements[tabIndex];
                        if (tabElement) {
                            tabElement.scrollIntoView({ inline: 'end' });
                        }
                    }
                }, 100);
            }
        }
    }

    private getTabTypeById(id: string): string {
        return this._tabs.find(tab => tab.tabId === id)?.content.type || '';
    }
}
